import { type ReactNode, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { formatDateShortLocalized } from '@/utils/date';
import { parseLocale } from '@/utils/localeFormatters';
import { withSentry } from '@/utils/withSentry';

// The 4xx exceptions thrown by `backend/src/v2/internalApi/fishGroups/exceptions`
// serialize as `{ statusCode, message, code, ...context }`. We extract the
// `code` and look up a user-friendly translation under
// `fishGroups.actions.errors.codes.<camelCaseCode>`, with the context fields as
// its values (`{{cageName}}`). Anything else is put into
// words the way every other form does it (`ApiErrorMessage`, with the generic
// copy as its fallback) and reported to Sentry so the missing mapping or the
// backend bug is visible.
export function useServerErrorMessage(error: unknown): ReactNode {
    const { t, i18n } = useTranslation('fishGroups');

    const body = useMemo(() => extractErrorBody(error), [error]);
    const errorCode = body?.code ?? null;

    useEffect(() => {
        if (!error || errorCode) return;
        // Dynamic import matches the lazy pattern used in initializeAnalyticsTools.
        // Sentry no-ops if it hasn't been initialised (dev/test), so this is safe
        // to call unconditionally.
        withSentry((Sentry) => Sentry.captureException(error));
    }, [error, errorCode]);

    if (!error) return null;

    if (body && errorCode) {
        const i18nKey = messageKey(errorCode, body.context);
        return t(i18nKey, {
            ...contextValues(body.context, parseLocale(i18n.language)),
            defaultValue: t('actions.errors.generic'),
        });
    }

    return apiErrorContent(error, t('actions.errors.generic'));
}

interface ErrorBody {
    code: string | null;
    context: Record<string, unknown>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function extractErrorBody(error: unknown): ErrorBody | null {
    if (!isRecord(error) || !isRecord(error.response) || !isRecord(error.response.data)) {
        return null;
    }
    const { code, message: _message, statusCode: _statusCode, ...context } = error.response.data;
    return { code: typeof code === 'string' ? code : null, context };
}

const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/**
 * The exception's context as translation values: strings and numbers as sent, and an ISO
 * timestamp (`occupiedUntil`) as the short date the date fields show.
 */
function contextValues(context: Record<string, unknown>, locale: string): Record<string, string> {
    const values: Record<string, string> = {};
    for (const [name, value] of Object.entries(context)) {
        if (typeof value === 'number') values[name] = String(value);
        if (typeof value === 'string') {
            values[name] = ISO_TIMESTAMP.test(value)
                ? formatDateShortLocalized(value, locale)
                : value;
        }
    }
    return values;
}

// Backend codes are SCREAMING_SNAKE (e.g. `INVALID_OPERATION_DATE`); the
// project's i18n convention is camelCase, so we adapt at the boundary.
function codeToKey(code: string): string {
    return code.toLowerCase().replace(/_([a-z])/g, (_, ch: string) => ch.toUpperCase());
}

/**
 * An open blocking stay has no end (`occupiedUntil` null). The dated sentence would leave
 * `{{occupiedUntil}}` in the text, so that case uses the copy that says the pen still holds
 * the group.
 */
function messageKey(code: string, context: Record<string, unknown>): string {
    if (code === 'DESTINATION_PEN_OCCUPIED_SINCE' && context.occupiedUntil == null) {
        return 'actions.errors.codes.destinationPenOccupiedSinceOpen';
    }
    return `actions.errors.codes.${codeToKey(code)}`;
}
